/*
 * This file is part of the Det Norske Teatret Nettside 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useState, useEffect, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import isEmail from 'validator/lib/isEmail';
import useDeepCompare from 'utils/useDeepCompare';
import supportsDatePicker from 'utils/datePickerSupported';
import isValidDate from 'utils/isValidDate';
import nextTick from 'utils/nextTick';
import Field from './Field/Field';

function getDefaultState(fields) {
  const state = {};
  fields.forEach(field => {
    if (typeof field.value === 'undefined') {
      if (field.type === 'choices' && field.multiple) {
        state[field.id] = [];
      } else if (field.type === 'booleanList') {
        state[field.id] = false;
      } else {
        state[field.id] = '';
      }
    } else if (Array.isArray(field.value)) {
      state[field.id] = [...field.value];
    } else if (typeof field.value === 'object') {
      state[field.id] = { ...field.value };
    } else {
      state[field.id] = field.value;
    }
  });

  return state;
}

function getDefaultErrors(fields) {
  const errors = {};
  fields.forEach(field => {
    errors[field.id] = null;
  });

  return errors;
}

/**
 * This is the Form component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function Form({
  forwardedRef,
  className,
  fields,
  errors: messages,
  children,
  actions,
  submit,
  disabled,
  onChange,
  onFocus,
  onBlur,
  onSubmit,
  onError,
}) {
  const [state, setState] = useState(getDefaultState(fields));
  const [errors, setErrors] = useState(getDefaultErrors(fields));

  useEffect(() => {
    setState(getDefaultState(fields));
  }, [useDeepCompare(fields)]);

  function validateField({ id, type, multiple, pattern, required }) {
    let error = null;
    const value = typeof value === 'string' ? state[id].trim() : state[id];

    if (required) {
      if (type === 'choices' && multiple) {
        if (value.length === 0) {
          error = 'required';
        }
      } else if (!value) {
        error = 'required';
      }
    }

    if (!error && type === 'email') {
      if (!isEmail(value)) {
        error = 'invalid';
      }
    }

    if (!error && type === 'date' && value.length) {
      if (supportsDatePicker) {
        if (!isValidDate(value)) {
          error = 'invalid';
        }
      } else {
        if (pattern) {
          const re = new RegExp(pattern);
          if (!value.match(re)) {
            error = 'invalid';
          }
        } else {
          if (!isValidDate(value)) {
            error = 'invalid';
          }
        }
      }
    }

    if (!error && type === 'choices' && multiple) {
      if (!Array.isArray(value)) {
        error = 'invalid';
      }
    }

    setErrors(prev => ({
      ...prev,
      [id]: error,
    }));

    return !error;
  }

  function validate() {
    let valid = true;

    fields.forEach(field => {
      if (!validateField(field)) {
        valid = false;
      }
    });

    return valid;
  }

  function getError({ id, label, errors: fieldErrors }) {
    if (!errors[id]) return null;
    const key = errors[id];

    if (fieldErrors) {
      if (fieldErrors[key]) {
        return fieldErrors[key].replace('{field}', label.toLowerCase());
      }
    }

    if (messages[key]) {
      return messages[key].replace('{field}', label.toLowerCase());
    }
    return key;
  }

  useImperativeHandle(forwardedRef, () => ({
    reset: () => {
      setState(getDefaultState(fields));
    },
    validate,
    getState: () => state,
  }));

  return (
    <form
      className={className}
      disabled={disabled}
      noValidate
      onSubmit={e => {
        e.preventDefault();
        if (validate()) {
          onSubmit(state);
        } else {
          nextTick(() => {
            onError();
          });
        }
      }}
    >
      {fields.map(({ id, ...props }) => (
        <Field
          key={id}
          id={id}
          {...props}
          value={state[id]}
          disabled={disabled}
          error={getError({ id, ...props })}
          onChange={({ target: { id: overriddenId, value } }) => {
            const resolvedId = overriddenId || id;
            setState(state => ({
              ...state,
              [resolvedId]: value,
            }));
            setErrors(state => ({
              ...state,
              [resolvedId]: null,
            }));
            onChange({
              id: resolvedId,
              ...props,
              value,
            });
          }}
          onFocus={() => {
            setErrors(state => ({
              ...state,
              [id]: null,
            }));
            onFocus({
              id,
              ...props,
            });
          }}
          onBlur={() => {
            validateField({ id, ...props });
            onBlur({
              id,
              ...props,
            });
          }}
        />
      ))}
      {children}
      <div className="actions">
        {actions}
        <button
          type="submit"
          name="submit"
          disabled={disabled}
          className={`button ${submit.color}`}
        >
          {submit.text}
        </button>
      </div>
    </form>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Form.propTypes = {
  forwardedRef: PropTypes.object,
  className: PropTypes.string,
  fields: PropTypes.array.isRequired,
  errors: PropTypes.shape({
    required: PropTypes.string.isRequired,
    invalid: PropTypes.string.isRequired,
  }).isRequired,
  actions: PropTypes.any,
  submit: PropTypes.shape({
    text: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
  }).isRequired,
  children: PropTypes.any,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Form.defaultProps = {
  forwardedRef: { current: null },
  fields: [],
  actions: null,
  errors: {
    required: 'Required {field}',
    invalid: 'Invalid {field}',
  },
  disabled: false,
  submit: {
    text: 'Submit',
    color: 'maroon',
  },
  onChange: () => {},
  onFocus: () => {},
  onBlur: () => {},
  onSubmit: () => {},
  onError: () => {},
};

/**
 * Triks for å omforme submit som tekst. Prosjektet er stort og jeg husker ikke alle stedene man kanskje
 * har angitt submit som streng. Nå må man også sende inn farge.
 */
function withSubmitTransform(WrappedComponent) {
  return props => {
    let submit = {
      ...WrappedComponent.defaultProps.submit,
    };

    if (typeof props.submit === 'object') {
      submit = {
        ...submit,
        ...props.submit,
      };
    }

    if (typeof props.submit === 'string') {
      submit = {
        ...submit,
        text: props.submit,
      };
    }

    return <WrappedComponent {...props} submit={submit} />;
  };
}

Form = withSubmitTransform(Form);

export default React.forwardRef((props, ref) => (
  <Form {...props} forwardedRef={ref} />
));
